import type { MapMarkerData, MapSegmentData } from '~/components/map';
import { getTransportMapIcon } from '~/constants/transport-modes';
import type { TripOverviewItem } from '~/services/engine-ts';
import type { GeoCoordinates } from '~/types/GeoLocation.types';
import { legTransportMode } from './transformOverviewItems';

export type TripMapData = {
  markers: MapMarkerData[];
  segments: MapSegmentData[];
  route: GeoCoordinates[];
  itemIdToProgress: Map<number, number>;
};

export type TripOverviewToMapDataOptions = {
  maxMiddleMarkers?: number;
  middleMarkers?: 'stays' | 'legs';
};

const sameCoords = (a: GeoCoordinates, b: GeoCoordinates) => a.latitude === b.latitude && a.longitude === b.longitude;

function haversineKm(a: GeoCoordinates, b: GeoCoordinates): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const x =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(x));
}

const originOf = (item: TripOverviewItem) => (item.type === 'leg' ? item.origin : item.place);

const destinationOf = (item: TripOverviewItem) => (item.type === 'leg' ? item.destination : item.place);

function evenlySpacedSubset<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr;
  if (n <= 0) return [];
  if (n === 1) return [arr[Math.floor(arr.length / 2)]];
  return Array.from({ length: n }, (_, i) => arr[Math.round((i * (arr.length - 1)) / (n - 1))]);
}

const buildMiddleMarkers = (
  items: TripOverviewItem[],
  originCoordinates: GeoCoordinates,
  progressFor: (itemId: number) => number
): MapMarkerData[] => {
  const middleMarkers: MapMarkerData[] = [];
  const seenCoords: GeoCoordinates[] = [originCoordinates];
  const alreadySeen = (coords: GeoCoordinates) => seenCoords.some((s) => sameCoords(s, coords));

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const isLast = i === items.length - 1;
    if (item.type === 'leg') continue;
    if (i === 0 || isLast) continue;
    if (alreadySeen(item.place.geoCoordinates)) continue;

    middleMarkers.push({
      id: `${item.type}-${item.itemId}`,
      icon: item.type === 'stay' ? 'bed' : 'pin',
      position: 'middle',
      coordinates: item.place.geoCoordinates,
      label: item.place.name,
      progressAt: progressFor(item.itemId)
    });
    seenCoords.push(item.place.geoCoordinates);
  }

  return middleMarkers;
};

const buildLegMiddleMarkers = (
  items: TripOverviewItem[],
  originCoordinates: GeoCoordinates,
  destinationCoordinates: GeoCoordinates,
  progressFor: (itemId: number) => number
): MapMarkerData[] => {
  const middleMarkers: MapMarkerData[] = [];
  const seenCoords: GeoCoordinates[] = [originCoordinates];
  const alreadySeen = (coords: GeoCoordinates) => seenCoords.some((s) => sameCoords(s, coords));

  for (const item of items) {
    if (item.type !== 'leg') continue;
    const coords = item.destination.geoCoordinates;
    if (sameCoords(coords, destinationCoordinates)) continue;
    if (alreadySeen(coords)) continue;

    middleMarkers.push({
      id: `station-${item.itemId}`,
      icon: getTransportMapIcon(legTransportMode(item)),
      position: 'middle',
      coordinates: coords,
      label: item.destination.name,
      progressAt: progressFor(item.itemId)
    });
    seenCoords.push(coords);
  }

  return middleMarkers;
};

export function tripOverviewToMapData(
  items: TripOverviewItem[],
  options: TripOverviewToMapDataOptions = {}
): TripMapData {
  if (items.length === 0) {
    return { markers: [], segments: [], route: [], itemIdToProgress: new Map() };
  }

  const first = items[0];
  const last = items.at(-1) ?? first;

  const originPlace = originOf(first);
  const destinationPlace = destinationOf(last);

  const segments: MapSegmentData[] = [];
  const route: GeoCoordinates[] = [originPlace.geoCoordinates];

  const itemIdToRouteIndex = new Map<number, number>();
  itemIdToRouteIndex.set(first.itemId, 0);

  let routeIndex = 0;
  for (const item of items) {
    if (item.type === 'leg') {
      segments.push({
        id: `leg-${item.itemId}`,
        origin: item.origin.geoCoordinates,
        destination: item.destination.geoCoordinates
      });
      route.push(item.destination.geoCoordinates);
      routeIndex += 1;
      itemIdToRouteIndex.set(item.itemId, routeIndex);
    } else if (!itemIdToRouteIndex.has(item.itemId)) {
      itemIdToRouteIndex.set(item.itemId, routeIndex);
    }
  }

  const cumulativeKm: number[] = [0];
  for (let i = 1; i < route.length; i++) {
    cumulativeKm.push(cumulativeKm[i - 1] + haversineKm(route[i - 1], route[i]));
  }
  const totalKm = (cumulativeKm.at(-1) ?? 0) || 1;
  const progressFor = (itemId: number) => (cumulativeKm[itemIdToRouteIndex.get(itemId) ?? 0] ?? 0) / totalKm;

  const itemIdToProgress = new Map<number, number>();
  for (const [itemId, idx] of itemIdToRouteIndex) {
    itemIdToProgress.set(itemId, (cumulativeKm[idx] ?? 0) / totalKm);
  }

  const originMarker: MapMarkerData = {
    id: 'origin',
    icon: 'home',
    position: 'start',
    coordinates: originPlace.geoCoordinates,
    label: originPlace.name,
    progressAt: 0
  };

  const middleMarkers =
    options.middleMarkers === 'legs'
      ? buildLegMiddleMarkers(items, originPlace.geoCoordinates, destinationPlace.geoCoordinates, progressFor)
      : buildMiddleMarkers(items, originPlace.geoCoordinates, progressFor);

  const trimmedMiddle =
    options.maxMiddleMarkers !== undefined
      ? evenlySpacedSubset(middleMarkers, options.maxMiddleMarkers)
      : middleMarkers;

  const isRoundTrip = sameCoords(originPlace.geoCoordinates, destinationPlace.geoCoordinates);

  const markers: MapMarkerData[] = [originMarker, ...trimmedMiddle];

  if (!isRoundTrip) {
    markers.push({
      id: 'destination',
      icon: 'flag',
      position: 'end',
      coordinates: destinationPlace.geoCoordinates,
      label: destinationPlace.name,
      progressAt: 1
    });
  }

  return { markers, segments, route, itemIdToProgress };
}
